import { useEffect } from "react";

/**
 * Check if an element is currently visible in the viewport.
 */
function isElementInViewport(element: Element): boolean {
  const rect = element.getBoundingClientRect();
  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
    rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  );
}

/**
 * Hook that handles asynchronous log line navigation.
 * Waits for log line elements to appear in the DOM and scrolls to them,
 * but only if they are not already visible in the viewport.
 *
 * Note: This hook is only used by the classic build UI (JobsPage) and the standalone
 * job log page (LogViewer). The new build UI uses VirtualisedLogOutput which handles
 * scrolling differently.
 */
export function useLogLineNavigation({ group, line }) {
  useEffect(() => {
    if (!line && !group) {
      return;
    }

    const targetLineNumber = line || group;
    const selector = `[data-line-number="${targetLineNumber}"]`;

    const scrollToElementIfNotVisible = (element: Element) => {
      if (!isElementInViewport(element)) {
        element.scrollIntoView();
      }
    };

    const waitForElementAndScroll = () => {
      // Check if element already exists
      const existingElement = document.querySelector(selector);
      if (existingElement) {
        scrollToElementIfNotVisible(existingElement);
        return;
      }

      // Set up MutationObserver to watch for the element
      const observer = new MutationObserver(() => {
        const foundElement = document.querySelector(selector);
        if (foundElement) {
          observer.disconnect();
          scrollToElementIfNotVisible(foundElement);
        }
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });

      // Just in case the content doesn't load we'll clean up the observer after a reasonable wait time
      const timeoutId = setTimeout(() => {
        observer.disconnect();
      }, 5000);

      return () => {
        observer.disconnect();
        clearTimeout(timeoutId);
      };
    };

    const cleanup = waitForElementAndScroll();
    return cleanup;
  }, [group, line]);
}
